import { yupResolver } from '@hookform/resolvers/yup';
import { useCalculusTFunction } from '@retail/calculus/i18n';
import { ConditionsNew } from '@retail/calculus/types';
import {
  CustomerMarkupConditionValueForm,
  editCustomerMarkupSchema,
  EditCustomerMarkupSchema,
} from '@retail/customer-markup/components';
import { useCustomerMarkupCreate } from '@retail/customer-markup/context';
import { EnteredField } from '@retail/customer-markup/types';
import { calculateFromPercentage, calculateToPercentage, getISO_8601Date } from '@retail/utils';
import { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { EditConditionDialog } from '../ConditionActionsBase';

interface Props {
  open: boolean;
  customerMarkupToEdit: ConditionsNew;
  assortmentId: number;
  onClose: () => void;
}

export function EditCustomerMarkupDialog({
  open,
  customerMarkupToEdit,
  assortmentId,
  onClose,
}: Props) {
  const t = useCalculusTFunction();
  const { createCustomerMarkupCondition } = useCustomerMarkupCreate({
    assortmentIds: [assortmentId],
  });

  const formMethods = useForm<EditCustomerMarkupSchema>({
    defaultValues: {
      factor: customerMarkupToEdit.customerMarkupCondition?.salesFactor
        ? customerMarkupToEdit.customerMarkupCondition?.salesFactor
        : customerMarkupToEdit.customerMarkupCondition?.percent
        ? Number(
            calculateFromPercentage(customerMarkupToEdit.customerMarkupCondition?.percent).toFixed(
              4
            )
          )
        : undefined,
      percent: customerMarkupToEdit.customerMarkupCondition?.percent
        ? customerMarkupToEdit.customerMarkupCondition?.percent
        : customerMarkupToEdit.customerMarkupCondition?.salesFactor
        ? Number(
            calculateToPercentage(
              customerMarkupToEdit.customerMarkupCondition?.salesFactor
            ).toFixed(2)
          )
        : undefined,
      enteredField: customerMarkupToEdit.customerMarkupCondition?.salesFactor
        ? EnteredField.FACTOR
        : EnteredField.PERCENT,
      fromDate: new Date(),
    },
    resolver: yupResolver(editCustomerMarkupSchema(t)),
  });

  const onActivate = useCallback(
    ({ fromDate, factor, percent, enteredField }: EditCustomerMarkupSchema) => {
      createCustomerMarkupCondition({
        customerMarkup: {
          percent: percent && enteredField === 'PERCENT' ? Number(percent) : undefined,
          factor: factor && enteredField === 'FACTOR' ? Number(factor) : undefined,
        },
        binding: customerMarkupToEdit.getBindingLevel(),
        validFrom: getISO_8601Date(fromDate),
      });

      onClose();
    },
    [createCustomerMarkupCondition, customerMarkupToEdit, onClose]
  );

  return (
    <EditConditionDialog
      open={open}
      title={t('calculus.customerMarkup.edit', { name: customerMarkupToEdit?.bindingTargetName })}
      dialogContent={
        <FormProvider {...formMethods}>
          <CustomerMarkupConditionValueForm />
        </FormProvider>
      }
      isDisabled={Object.keys(formMethods?.formState?.errors).length > 0}
      onSubmit={formMethods.handleSubmit(onActivate)}
      onClose={onClose}
    />
  );
}
